<template>
  <div class="d3-graph-legend" :class="{ 'dark-theme': isDark }">
    <div v-for="item in legendItems" :key="item.type" class="legend-item">
      <span class="legend-dot" :class="`dot-${item.type}`" />
      <span class="legend-label">{{ item.label }}</span>
      <span v-if="getCount(item.type) > 0" class="legend-count">({{ getCount(item.type) }})</span>
      <!-- Severity info icon for CVE -->
      <v-menu v-if="item.type === 'cve' && hasSeverityData" open-on-hover offset-y top :close-on-content-click="false">
        <template v-slot:activator="{ on, attrs }">
          <v-icon x-small class="severity-info-icon" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
        </template>
        <div class="severity-tooltip" :class="{ 'dark-theme': isDark }">
          <div class="severity-tooltip__title">CVE Severity Breakdown</div>
          <div class="severity-tooltip__items">
            <div v-if="severityCounts.critical > 0" class="severity-row">
              <span class="severity-dot critical" />
              <span class="severity-label">Critical</span>
              <span class="severity-value">{{ severityCounts.critical }}</span>
            </div>
            <div v-if="severityCounts.high > 0" class="severity-row">
              <span class="severity-dot high" />
              <span class="severity-label">High</span>
              <span class="severity-value">{{ severityCounts.high }}</span>
            </div>
            <div v-if="severityCounts.medium > 0" class="severity-row">
              <span class="severity-dot medium" />
              <span class="severity-label">Medium</span>
              <span class="severity-value">{{ severityCounts.medium }}</span>
            </div>
            <div v-if="severityCounts.low > 0" class="severity-row">
              <span class="severity-dot low" />
              <span class="severity-label">Low</span>
              <span class="severity-value">{{ severityCounts.low }}</span>
            </div>
            <div v-if="severityCounts.none > 0" class="severity-row">
              <span class="severity-dot none" />
              <span class="severity-label">Unknown</span>
              <span class="severity-value">{{ severityCounts.none }}</span>
            </div>
          </div>
          <div class="severity-tooltip__hint">
            <v-icon x-small class="hint-icon">mdi-circle-outline</v-icon>
            <span>CVE nodes have colored rings matching their severity</span>
          </div>
        </div>
      </v-menu>
    </div>
  </div>
</template>

<script>
import { D3_NODE_TYPES } from './d3GraphUtils';

export default {
  name: 'D3GraphLegend',
  props: {
    isDark: { type: Boolean, default: false },
    nodeCounts: { type: Object, default: () => ({}) },
    severityCounts: {
      type: Object,
      default: () => ({
        critical: 0, high: 0, medium: 0, low: 0, none: 0,
      }),
    },
  },
  computed: {
    legendItems() {
      return [
        { type: D3_NODE_TYPES.ORG, label: 'Org' },
        { type: D3_NODE_TYPES.ASSET, label: 'Asset' },
        { type: D3_NODE_TYPES.TECH, label: 'Tech' },
        { type: D3_NODE_TYPES.CVE, label: 'CVE' },
        { type: D3_NODE_TYPES.CWE, label: 'CWE' },
        { type: D3_NODE_TYPES.CAPEC, label: 'CAPEC' },
        { type: D3_NODE_TYPES.TTP, label: 'TTP' },
        { type: D3_NODE_TYPES.APT, label: 'APT' },
      ];
    },
    hasSeverityData() {
      const {
        critical, high, medium, low,
      } = this.severityCounts;
      return critical > 0 || high > 0 || medium > 0 || low > 0;
    },
  },
  methods: {
    getCount(type) {
      return this.nodeCounts[type] || 0;
    },
  },
};
</script>

<style lang="scss" scoped>
.d3-graph-legend {
  position: absolute;
  bottom: 12px;
  left: 12px;
  display: flex;
  gap: 14px;
  padding: 10px 14px;
  background: var(--bg-primary);
  border-radius: 6px;
  border: 1px solid var(--border-primary);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  z-index: 10;
  transition: background 0.3s ease, border-color 0.3s ease;

  &.dark-theme {
    backdrop-filter: blur(4px);
  }
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: var(--text-secondary);
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;

  &.dot-org { background-color: var(--node-org); }
  &.dot-asset { background-color: var(--node-asset); }
  &.dot-tech { background-color: var(--node-tech); }
  &.dot-cve { background-color: var(--node-cve); }
  &.dot-cwe { background-color: var(--node-cwe); }
  &.dot-capec { background-color: var(--node-capec); }
  &.dot-ttp { background-color: var(--node-ttp); }
  &.dot-apt { background-color: var(--node-apt); }
}

.legend-label {
  white-space: nowrap;
  color: var(--text-primary);
  font-weight: 500;
}

.legend-count {
  color: var(--text-muted);
  font-size: 10px;
}

.severity-info-icon {
  color: var(--text-muted);
  cursor: pointer;
  opacity: 0.7;
  margin-left: -2px;
  margin-right: 6px;

  &:hover {
    opacity: 1;
  }
}

.severity-tooltip {
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  padding: 10px 12px;
  min-width: 160px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

  &.dark-theme {
    background: #1e1e2e;
    border-color: rgba(255, 255, 255, 0.1);
  }

  &__title {
    font-size: 11px;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 8px;
    padding-bottom: 6px;
    border-bottom: 1px solid var(--border-primary);
  }

  &__items {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
}

.severity-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
}

.severity-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;

  &.critical { background-color: #ef4444; }
  &.high { background-color: #f97316; }
  &.medium { background-color: #eab308; }
  &.low { background-color: #22c55e; }
  &.none { background-color: #9ca3af; }
}

.severity-label {
  flex: 1;
  color: var(--text-secondary);
}

.severity-value {
  font-weight: 600;
  color: var(--text-primary);
  min-width: 24px;
  text-align: right;
}

.severity-tooltip__hint {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid var(--border-primary);
  font-size: 10px;
  color: var(--text-muted);
  font-style: italic;

  .hint-icon {
    color: var(--text-muted);
    opacity: 0.7;
  }
}
</style>
